import { useContext, useMemo } from 'react';
import { FeatureFlags, Filter } from '@fabric-ui/components';
import { checkIfSeverityFilter, sortBySeverity } from '@utils/filterUtils';
import { buildAliasResolution, getDimsWithAlias, mapAliasToName, parseWidgetDataBeforeApiRequest } from '@utils/utils';
import { useAvContext } from '../../../context/AvContextProvider';
import { AsyncQueryStatus, DwQueryResponse } from '../../../hooks/queryObjectAsync/useQueryObjectAsyncWithCachedResults.utils';
import { isUnapplicableFilterError } from '../components/Widget/unapplicableFilter.utils';
import { CustomDashboardContext } from '../CustomDashboardContext';
import { isHistoricalQuery, isValidTimeRange, shouldEnableWidgetQuery, shouldShowTotals } from '../CustomDashboardsUtils';
import { useQueryObjectWithHistoricalAsync } from '../hooks';
import { AllTypeWidgets, FeatureFlagsOverrides, HistoricalFilterMode } from '../types';

const getOverrideSortedData = (data: Record<string, string>[], widget) => {
  const severitySortField = widget.requests[0].select.dims.find(({ name }) => checkIfSeverityFilter(name));
  return sortBySeverity({
    data,
    severityFieldName: severitySortField?.name,
    key: severitySortField?.name,
    direction: widget.requests[0].sorting[0]?.dir,
  });
};

const isHistoricalEvaluationFilterMode = (widget: AllTypeWidgets, featureFlags: Record<string, unknown>) =>
  Boolean(
    featureFlags[FeatureFlags.WidgetHistoricalFilterMode] &&
    widget.historicalFilterMode === HistoricalFilterMode.HISTORICAL &&
    isHistoricalQuery(widget.requests[0])
  );

const contextFilterWithEvaluation = (widget: AllTypeWidgets, featureFlags: Record<string, boolean>, contextFilter?: Filter) =>
  isHistoricalEvaluationFilterMode(widget, featureFlags) && contextFilter ? { ...contextFilter, isHistorical: true } : contextFilter;

export const getWidgetQueryResponse = ({
  asyncResult,
  asyncQueryStatus,
  data,
  queryId,
  totals,
  totalRowCount,
}: {
  asyncResult?: DwQueryResponse['asyncResult'];
  asyncQueryStatus?: AsyncQueryStatus;
  data: any;
  queryId?: string;
  totals: any;
  totalRowCount?: number;
}): DwQueryResponse | undefined => {
  const isComplete = asyncResult === 'ASYNC_DONE' || asyncQueryStatus === AsyncQueryStatus.Done;
  if (!isComplete || !Array.isArray(data)) return undefined;
  const rows = data.map(({ id, ...rest }) => ({ data: rest, id }));
  return {
    rows,
    queryId,
    totals: (totals ?? null) as DwQueryResponse['totals'],
    totalRowCount: totalRowCount ?? null,
    asyncResult: 'ASYNC_DONE',
  };
};

const withoutIncompleteFilterValues = (filter?: Filter): Filter | undefined => {
  if (!filter) return undefined;
  const operator = filter.and ? 'and' : filter.or ? 'or' : undefined;
  if (operator) {
    const operands = filter[operator]!.operands.map(withoutIncompleteFilterValues).filter(Boolean) as Filter[];
    return operands.length ? { ...filter, [operator]: { ...filter[operator], operands } } : undefined;
  }

  const expression = filter.expression?.arrayCondition?.underlying ?? filter.expression;
  const hasIncompleteValue = Object.entries(expression ?? {}).some(
    ([key, condition]) =>
      key !== 'fieldName' &&
      condition !== null &&
      typeof condition === 'object' &&
      Object.entries(condition).some(
        ([conditionKey, value]) => value === null || value === undefined || (conditionKey === 'contains' && value === '')
      )
  );
  return hasIncompleteValue ? undefined : filter;
};

export const useGetWidgetData = ({
  widget,
  contextFilter,
  visible = true,
  debounceQueries = false,
}: {
  widget: AllTypeWidgets;
  contextFilter?: Filter;
  visible?: boolean;
  debounceQueries?: boolean;
}) => {
  const { featureFlags } = useAvContext();
  const { metricsWithAliases, globalInteractiveFilters } = useContext(CustomDashboardContext);

  const validTimeRange = isValidTimeRange(widget.requests?.[0]?.timeRange);
  const showTotals = shouldShowTotals(widget);
  const shouldEnableQuery = shouldEnableWidgetQuery({ widget });
  const enabled = validTimeRange && visible && !!widget.requests.length && shouldEnableQuery;

  const widgetForQuery = useMemo(
    () => ({
      ...widget,
      requests: widget.requests.map(request =>
        request.filter ? { ...request, filter: withoutIncompleteFilterValues(request.filter) ?? null } : request
      ),
    }),
    [widget]
  );
  const finalWidget = useMemo(
    () => parseWidgetDataBeforeApiRequest({ widget: widgetForQuery, metricsWithAliases }) as AllTypeWidgets,
    [widgetForQuery, metricsWithAliases]
  );

  if (isHistoricalEvaluationFilterMode(widget, featureFlags)) {
    finalWidget.requests = finalWidget.requests.map(req => (req.filter ? { ...req, filter: { ...req.filter, isHistorical: true } } : req));
  }

  const aliasResolution = useMemo(
    () => buildAliasResolution({ dims: globalInteractiveFilters, contextFilter }),
    [globalInteractiveFilters, contextFilter]
  );

  const featureFlagsOverrides: FeatureFlagsOverrides = {};

  if (widget.binnedTimeBuckets) {
    featureFlagsOverrides.binnedTimeBuckets = true;
  }

  if (widget.queryAggIfCollapseOptimization) {
    featureFlagsOverrides.queryAggIfCollapseOptimization = true;
  }

  const results = useQueryObjectWithHistoricalAsync({
    queryObject: finalWidget.requests,
    showTotals,
    aliasResolution,
    contextFilter: contextFilterWithEvaluation(widget, featureFlags, contextFilter),
    totalRowCount: true,
    debounceQueries,
    options: { enabled },
    tags: { widgetId: widget.id },
    featureFlagsOverrides,
    errorSnackbarSuppressor: isUnapplicableFilterError,
  });

  const {
    totals,
    data = [],
    isLoading,
    isRefetching,
    errors,
    totalRowCount,
    isAsyncQuery,
    isDebouncing,
    responseCalculatedAt,
    asyncQueryStatus,
    asyncResult,
    queryId,
  } = results;
  const isSeverityFilter = checkIfSeverityFilter(widget.requests[0].sorting[0]?.name || '');
  if (isSeverityFilter && !Array.isArray(data)) {
    console.warn('Non array severity data: ', data);
  }

  const dimsWithAlias = getDimsWithAlias(widget.requests[0].select.dims);

  const getParsedData = data => {
    const parsedData = mapAliasToName(data, dimsWithAlias);

    if (isSeverityFilter && Array.isArray(parsedData)) {
      return getOverrideSortedData(parsedData, widget);
    }

    return parsedData;
  };

  const widgetQueryResponse = useMemo(
    () => getWidgetQueryResponse({ asyncResult, asyncQueryStatus, data, queryId, totals, totalRowCount }),
    [asyncResult, asyncQueryStatus, queryId, data, totals, totalRowCount]
  );

  return {
    data: getParsedData(data),
    totals,
    isLoading,
    isRefetching,
    errors,
    totalRowCount,
    isAsyncQuery,
    isDebouncing,
    responseCalculatedAt,
    asyncQueryStatus,
    queryId,
    widgetQueryResponse,
  } as const;
};
